import { useCallback, useEffect, useState } from "react";
import { KeyRound, Lock, Pencil, Plus, Trash2, Unlock, UserRound } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/layout/AppShell";
import { adminSubnav } from "@/lib/subnavs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { PermissionsEditor } from "@/components/users/PermissionsEditor";

const EMPTY = { prenom: "", nom: "", matricule: "", pin: "", is_admin: false, active: true, pin_requis_admin: true, has_pin: false };

export default function UsersPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [edit, setEdit] = useState(null); // {mode:'new'|'edit', form, id}
  const [catalogue, setCatalogue] = useState(null); // { groupes, defauts }
  useEffect(() => { api.get("/users/permissions").then(({ data }) => setCatalogue(data)).catch(() => {}); }, []);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/users"); setUsers(data); } catch (e) { toast.error(errMsg(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    const f = edit.form;
    try {
      if (edit.mode === "new") {
        await api.post("/users", {
          prenom: f.prenom, nom: f.nom, matricule: f.matricule, is_admin: f.is_admin,
          pin_requis: f.pin_requis_admin, pin: f.is_admin && f.pin ? f.pin : null,
          permissions: f.permissions_reglees,
        });
        toast.success("Utilisateur créé");
      } else {
        const payload = { prenom: f.prenom, nom: f.nom, matricule: f.matricule, is_admin: f.is_admin, active: f.active, pin_requis: f.pin_requis_admin };
        if (!f.is_admin && f.permissions_reglees) payload.permissions = f.permissions_reglees;
        if (f.is_admin && f.pin) payload.pin = f.pin;
        await api.put(`/users/${edit.id}`, payload);
        toast.success("Utilisateur mis à jour");
      }
      setEdit(null);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const unlock = async (u) => {
    try { await api.post(`/users/${u.id}/unlock`); toast.success("Compte débloqué"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (u) => {
    if (!window.confirm(`Supprimer définitivement ${u.prenom} ${u.nom} ?`)) return;
    try { await api.delete(`/users/${u.id}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  const setF = (k, v) => setEdit((d) => ({ ...d, form: { ...d.form, [k]: v } }));
  const locked = (u) => u.locked_until && new Date(u.locked_until) > new Date();

  return (
    <AppShell title="Administrateur" subnav={adminSubnav()}>
      <div className="flex items-end justify-between gap-3 mb-5">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Utilisateurs</h1>
          <p className="text-sm text-muted-foreground">Les administrateurs ont tous les droits ; pour les autres, choisissez les onglets et les actions permises (crayon › Permissions).</p>
        </div>
        <Button onClick={() => setEdit({ mode: "new", form: { ...EMPTY, permissions_reglees: { ...(catalogue?.defauts || {}) } } })}><Plus size={14} /> Nouvel utilisateur</Button>
      </div>

      <div className="rounded-xl border border-border bg-card overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow><TableHead>Nom</TableHead><TableHead>Matricule</TableHead><TableHead>Rôle</TableHead><TableHead>Connexion</TableHead><TableHead>État</TableHead><TableHead /></TableRow>
          </TableHeader>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id}>
                <TableCell className="font-medium">{u.prenom} {u.nom}{u.id === me?.id && <span className="text-xs text-muted-foreground"> (vous)</span>}</TableCell>
                <TableCell className="font-mono">{u.matricule}</TableCell>
                <TableCell>{u.is_admin ? <span className="text-xs font-semibold px-2 py-0.5 rounded bg-primary/15 text-primary">Administrateur</span> : <span className="text-sm text-muted-foreground">Utilisateur{catalogue && <span className="block text-[11px]" data-testid={`resume-perms-${u.matricule}`}>{Object.values(u.permissions || {}).filter(Boolean).length} / {Object.keys(catalogue.defauts).length} permissions</span>}</span>}</TableCell>
                <TableCell className="text-sm whitespace-nowrap" data-testid={`connexion-${u.matricule}`}>
                  {u.pin_requis
                    ? <span className="inline-flex items-center gap-1"><KeyRound size={13} className="text-primary" /> Matricule + code</span>
                    : <span className="inline-flex items-center gap-1 text-muted-foreground"><UserRound size={13} /> Matricule seul</span>}
                </TableCell>
                <TableCell className="text-sm">
                  {!u.active ? <span className="text-muted-foreground">Désactivé</span> : locked(u) ? <span className="text-destructive inline-flex items-center gap-1"><Lock size={13} /> Bloqué</span> : "Actif"}
                </TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  {locked(u) && <Button size="sm" variant="ghost" onClick={() => unlock(u)} title="Débloquer"><Unlock size={14} /></Button>}
                  <Button size="sm" variant="ghost" onClick={() => setEdit({ mode: "edit", id: u.id, form: { ...EMPTY, ...u, pin: "" } })}><Pencil size={14} /></Button>
                  {u.id !== me?.id && <Button size="sm" variant="ghost" className="text-destructive" onClick={() => del(u)}><Trash2 size={14} /></Button>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className={`${edit && !edit.form.is_admin ? "sm:max-w-2xl" : "sm:max-w-md"} flex flex-col gap-0 p-0 max-h-[90vh] overflow-hidden`}>
          {edit && (
            <form onSubmit={save} className="flex flex-col min-h-0 flex-1">
              {/* Contenu défilant ; boutons toujours visibles en bas */}
              <div className="overflow-y-auto min-h-0 flex-1 p-6 space-y-4" data-testid="user-form-scroll">
              <DialogHeader>
                <DialogTitle>{edit.mode === "new" ? "Nouvel utilisateur" : "Modifier l'utilisateur"}</DialogTitle>
                <DialogDescription>
                  Les utilisateurs se connectent avec leur matricule (7 chiffres) seul. Les administrateurs ont en plus un code (8 chiffres), qu'on peut rendre facultatif.
                </DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Prénom</Label><Input value={edit.form.prenom} onChange={(e) => setF("prenom", e.target.value)} required /></div>
                <div className="space-y-1.5"><Label>Nom</Label><Input value={edit.form.nom} onChange={(e) => setF("nom", e.target.value)} required /></div>
                <div className="space-y-1.5 col-span-2 sm:col-span-1"><Label>Matricule</Label><Input inputMode="numeric" value={edit.form.matricule} onChange={(e) => setF("matricule", e.target.value.replace(/\D/g, "").slice(0, 7))} required minLength={7} data-testid="user-matricule" /></div>
              </div>
              <label className="flex items-center justify-between gap-3 text-sm">
                Administrateur (tous les droits : zones, tournées, utilisateurs…)
                <Switch checked={edit.form.is_admin} onCheckedChange={(v) => setF("is_admin", v)} data-testid="user-admin" />
              </label>
              {edit.form.is_admin && (
                <div className="rounded-lg border border-border p-3 space-y-3" data-testid="user-code-bloc">
                  <label className="flex items-center justify-between gap-3 text-sm">
                    <span>
                      Code obligatoire à la connexion
                      <span className="block text-xs text-muted-foreground">Désactivé : cet administrateur se connecte avec son matricule seul.</span>
                    </span>
                    <Switch checked={edit.form.pin_requis_admin} onCheckedChange={(v) => setF("pin_requis_admin", v)} data-testid="user-pin-requis" />
                  </label>
                  <div className="space-y-1.5">
                    <Label>{edit.form.has_pin ? "Nouveau code (8 chiffres)" : "Code (8 chiffres)"}</Label>
                    <Input type="password" inputMode="numeric" value={edit.form.pin} onChange={(e) => setF("pin", e.target.value.replace(/\D/g, "").slice(0, 8))}
                      required={edit.form.pin_requis_admin && !edit.form.has_pin} minLength={8}
                      placeholder={edit.form.has_pin ? "inchangé" : ""} autoComplete="new-password" data-testid="user-pin" />
                  </div>
                </div>
              )}
              {!edit.form.is_admin && catalogue && (
                <div className="space-y-2">
                  <p className="text-sm font-medium">Permissions</p>
                  <PermissionsEditor groupes={catalogue.groupes} value={edit.form.permissions_reglees || catalogue.defauts}
                    onChange={(v) => setF("permissions_reglees", v)} />
                </div>
              )}
              {edit.mode === "edit" && (
                <label className="flex items-center justify-between gap-3 text-sm">
                  Compte actif
                  <Switch checked={edit.form.active} onCheckedChange={(v) => setF("active", v)} />
                </label>
              )}
              </div>
              <DialogFooter className="border-t border-border px-6 py-3 shrink-0">
                <Button type="button" variant="ghost" onClick={() => setEdit(null)}>Annuler</Button>
                <Button type="submit">Enregistrer</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
